import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, Field, FieldError, FieldLabel, Input, SegmentedTabs } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import {
  emptyRecurrence,
  useCreateStayPack,
  useUpdateStayPack,
  type StayPack,
  type StayPackFrequency,
  type StayPackRecurrence,
} from "@/hooks/useStayPacks";
import { ColorSwatchPicker } from "./primitives";

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);

export default function PackDialog({
  open,
  onOpenChange,
  pack,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` opens the dialog in create mode. */
  pack: StayPack | null;
}) {
  const { t } = useTranslation("daycareHotel");
  const create = useCreateStayPack();
  const update = useUpdateStayPack();

  const [name, setName] = useState("");
  const [color, setColor] = useState<string | null>("orange");
  const [recurrence, setRecurrence] = useState<StayPackRecurrence>(emptyRecurrence());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(pack?.name ?? "");
    setColor(pack?.color ?? "orange");
    setRecurrence(pack?.recurrence ?? emptyRecurrence());
    setError(null);
  }, [open, pack]);

  const weekdayLabels = t("packs.weekdayInitials", { returnObjects: true }) as string[];
  const isSaving = create.isPending || update.isPending;

  const toggleDayOfWeek = (day: number) =>
    setRecurrence((previous) => ({
      ...previous,
      daysOfWeek: previous.daysOfWeek.includes(day)
        ? previous.daysOfWeek.filter((value) => value !== day)
        : [...previous.daysOfWeek, day].sort((a, b) => a - b),
    }));

  const toggleDayOfMonth = (day: number) =>
    setRecurrence((previous) => ({
      ...previous,
      daysOfMonth: previous.daysOfMonth.includes(day)
        ? previous.daysOfMonth.filter((value) => value !== day)
        : [...previous.daysOfMonth, day].sort((a, b) => a - b),
    }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("packs.errorNameRequired"));
      return;
    }
    if (recurrence.frequency === "weekly" && recurrence.daysOfWeek.length === 0) {
      setError(t("packs.errorDaysRequired"));
      return;
    }
    if (recurrence.frequency === "monthly" && recurrence.daysOfMonth.length === 0) {
      setError(t("packs.errorDaysOfMonthRequired"));
      return;
    }

    const input = { name: trimmed, color, recurrence };
    try {
      if (pack) await update.mutateAsync({ id: pack.id, ...input });
      else await create.mutateAsync(input);
      toast({ title: pack ? t("packs.toastUpdated") : t("packs.toastCreated") });
      onOpenChange(false);
    } catch {
      toast({ variant: "destructive", title: t("packs.toastSaveFailed") });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={pack ? t("packs.dialogEditTitle") : t("packs.dialogCreateTitle")}
      description={t("packs.dialogDescription")}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <Field invalid={!!error}>
          <FieldLabel>{t("packs.nameLabel")}</FieldLabel>
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("packs.namePlaceholder")}
            autoFocus
          />
        </Field>

        <div>
          <span className="akuna-field__label mb-2 block">{t("packs.colorLabel")}</span>
          <ColorSwatchPicker value={color} onChange={setColor} label={t("packs.colorLabel")} />
        </div>

        <div>
          <span className="akuna-field__label mb-2 block">{t("packs.recurrenceLabel")}</span>
          <SegmentedTabs
            variant="outline-secondary"
            value={recurrence.frequency}
            onValueChange={(value) =>
              setRecurrence((previous) => ({ ...previous, frequency: value as StayPackFrequency }))
            }
            items={[
              { value: "daily", label: t("packs.frequencyDaily") },
              { value: "weekly", label: t("packs.frequencyWeekly") },
              { value: "monthly", label: t("packs.frequencyMonthly") },
            ]}
          />
          <p className="mt-2 text-xs text-color-light">
            {recurrence.frequency === "weekly"
              ? t("packs.weeklyHint")
              : recurrence.frequency === "daily"
                ? t("packs.dailyHint")
                : t("packs.monthlyHint")}
          </p>
        </div>

        {recurrence.frequency === "daily" ? null : recurrence.frequency === "weekly" ? (
          <div role="group" aria-label={t("packs.daysOfWeekLabel")} className="flex flex-wrap gap-1.5">
            {weekdayLabels.map((label, day) => {
              const selected = recurrence.daysOfWeek.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleDayOfWeek(day)}
                  className={cn(
                    "h-9 w-9 rounded-full border text-[12.5px] font-semibold transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selected
                      ? "border-akuna-orange bg-akuna-orange text-primary-foreground"
                      : "border-border-gray bg-card text-color-medium hover:bg-background-app",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : (
          <div role="group" aria-label={t("packs.daysOfMonthLabel")} className="flex flex-wrap gap-1.5">
            {DAYS_OF_MONTH.map((day) => {
              const selected = recurrence.daysOfMonth.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleDayOfMonth(day)}
                  className={cn(
                    "h-8 w-8 rounded-lg border text-xs font-semibold tabular-nums transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selected
                      ? "border-akuna-orange bg-akuna-orange text-primary-foreground"
                      : "border-border-gray bg-card text-color-medium hover:bg-background-app",
                  )}
                >
                  {day}
                </button>
              );
            })}
          </div>
        )}

        {error ? <FieldError>{error}</FieldError> : null}

        <div className="flex gap-3 pt-1">
          <Button type="button" variant="outline" fullWidth onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" fullWidth disabled={isSaving}>
            {isSaving ? t("common.saving") : t("packs.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
